import { AlertTriangle, Bot, Clock3, ShieldCheck, TrendingUp, Zap } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmActionExecutionQueue } from "@/hooks/use-crm-action-execution-queue";
import { formatMonthDay } from "@/lib/i18n";
import type {
  BulkMaterializeCrmActionQueueResult,
  BulkResolveManualCrmActionQueueResult,
  CrmActionExecutionQueueFilters,
  CrmActionExecutionQueueItem,
  CrmActionExecutionQueueStaleBucket,
  CrmActionExecutionQueueStatus,
  CrmActionExecutionQueueTotals,
  CrmActionQueueExecutionEligibilityState,
} from "@/hooks/use-crm-action-execution-queue";

const STATUS_LABEL: Record<CrmActionExecutionQueueStatus, string> = {
  ready_for_execution: "Ready",
  manual_review: "Manual review",
  blocked: "Blocked",
  unknown: "Unknown",
};

const STATUS_CLASS: Record<CrmActionExecutionQueueStatus, string> = {
  ready_for_execution: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  manual_review: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  blocked: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  unknown: "border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
};

const PRIORITY_LABEL: Record<string, string> = {
  outcome_backed_ready: "Outcome-backed",
  ready: "Ready priority",
  manual_review: "Review priority",
  already_materialized: "Already recorded",
  blocked: "Blocked",
  unknown: "Unranked",
};

const PRIORITY_CLASS: Record<string, string> = {
  outcome_backed_ready: "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  ready: "border-[var(--color-success)]/25 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  manual_review: "border-[var(--color-warning)]/25 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  already_materialized: "border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
  blocked: "border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  unknown: "border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
};

const STALE_BUCKETS: CrmActionExecutionQueueStaleBucket[] = ["fresh", "aging", "stale", "critical"];

const STALE_BUCKET_LABEL: Record<CrmActionExecutionQueueStaleBucket, string> = {
  fresh: "Fresh",
  aging: "Aging",
  stale: "Stale",
  critical: "Critical",
};

const STALE_BUCKET_CLASS: Record<CrmActionExecutionQueueStaleBucket, string> = {
  fresh: "border-[var(--color-success)]/25 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  aging: "border-[var(--color-warning)]/25 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  stale: "border-[var(--color-warning)]/35 bg-[var(--color-warning)]/15 text-[var(--color-warning)]",
  critical: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

const ELIGIBILITY_LABEL: Record<CrmActionQueueExecutionEligibilityState, string> = {
  executable: "Executable",
  manual_only: "Manual path",
  already_materialized: "Already done",
  blocked: "Blocked bridge",
  bridge_missing: "Bridge missing",
  not_ready: "Not ready",
};

const ELIGIBILITY_CLASS: Record<CrmActionQueueExecutionEligibilityState, string> = {
  executable: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  manual_only: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  already_materialized: "border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
  blocked: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  bridge_missing: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  not_ready: "border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
};

export type QueueView = "all" | "ready" | "outcome_backed" | "materializable" | "manual_only" | "review" | "bridge_blocked" | "blocked";

const QUEUE_VIEW_FILTERS: Record<QueueView, CrmActionExecutionQueueFilters> = {
  all: {},
  ready: { status: "ready_for_execution" },
  outcome_backed: { priority: "outcome_backed_ready" },
  materializable: { status: "ready_for_execution", resolution: "materializable" },
  manual_only: { status: "ready_for_execution", resolution: "manual_only" },
  review: { status: "manual_review" },
  bridge_blocked: { status: "ready_for_execution", resolution: "bridge_blocked" },
  blocked: { status: "blocked" },
};

const QUEUE_VIEW_LABEL: Record<QueueView, string> = {
  all: "All",
  ready: "Ready",
  outcome_backed: "Outcome-backed",
  materializable: "Materializable",
  manual_only: "Manual-only",
  review: "Review",
  bridge_blocked: "Bridge gaps",
  blocked: "Blocked",
};

const ALL_SOURCE_EVENTS_FILTER = "__all_source_events__";

const EMPTY_QUEUE_TOTALS: CrmActionExecutionQueueTotals = {
  total: 0,
  readyForExecution: 0,
  manualReview: 0,
  blocked: 0,
  unknown: 0,
  outcomeBackedReady: 0,
  materializableReady: 0,
  manualOnlyReady: 0,
  bridgeBlockedReady: 0,
  alreadyMaterialized: 0,
};

const BULK_SKIP_REASON_LABEL: Record<string, string> = {
  not_in_execution_queue: "not in queue",
  batch_limit: "batch limit",
  not_materializable: "not materializable",
  manual_only: "manual path",
  already_materialized: "already done",
  blocked: "blocked",
  bridge_missing: "bridge missing",
  not_ready: "not ready",
  materialization_endpoint_missing: "endpoint missing",
  not_manual_resolvable: "not manual-resolvable",
};

function prettyActionLabel(value: string | null | undefined): string {
  if (!value || value === "unknown") {
    return "Unknown action";
  }

  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatRate(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatQueueAge(ageHours: number | null | undefined, ageDays: number | null | undefined): string {
  if (ageHours == null) {
    return "Age unknown";
  }

  if (ageHours < 24) {
    return `${ageHours}h queued`;
  }

  return `${ageDays ?? Math.floor(ageHours / 24)}d queued`;
}

function prettyOperationLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function resultStatusLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.replace(/_/g, " ");
}

interface BulkResultStatusSummary {
  dryRun: boolean;
  results: Array<{
    status: "planned" | "succeeded" | "failed" | "skipped";
    resultStatus?: string | null;
  }>;
}

function statusOutcomeSummary(result: BulkResultStatusSummary): string {
  const counts = new Map<string, number>();
  for (const item of result.results) {
    if (item.status !== "succeeded") continue;
    const label = resultStatusLabel(item.resultStatus);
    if (!label) continue;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }

  const summary = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label, count]) => `${count} ${label}`)
    .join(", ");

  return summary ? ` (${summary})` : "";
}

function materializationResultStatus(value: unknown): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const status = (value as { status?: unknown }).status;
  return typeof status === "string" ? status : null;
}

function materializationStatusLabel(status: CrmActionExecutionQueueItem["materializationStatus"]): string {
  if (status === "existing") return "existing record";
  if (status === "created") return "created";
  if (status === "updated") return "updated";
  return "done";
}

function targetLinkForQueueItem(item: CrmActionExecutionQueueItem): { to: string; label: string } | null {
  if (item.contactId) {
    return { to: `/crm/contacts/${item.contactId}`, label: "Contact" };
  }
  if (item.targetEntityType === "deal" && item.targetEntityId) {
    return { to: `/crm/deals/${item.targetEntityId}`, label: "Deal" };
  }
  if (item.targetEntityType === "company" && item.targetEntityId) {
    return { to: `/crm/companies/${item.targetEntityId}`, label: "Company" };
  }
  if (item.targetEntityType === "task") {
    return { to: "/crm/tasks", label: "Task" };
  }
  if (item.targetEntityType === "activity") {
    return { to: "/crm/activities", label: "Activity" };
  }
  return null;
}

function itemCanBulkMaterialize(item: CrmActionExecutionQueueItem): boolean {
  return item.executionEligibility?.canExecute ?? Boolean(
    item.canMaterializeTask
    || item.canMaterializeTaskUpdate
    || item.canMaterializeActivity
    || item.canMaterializeTag
    || item.canMaterializeDeal
    || item.canMaterializePersonCreate
    || item.canMaterializePersonUpdate
    || item.canMaterializeCompanyUpdate,
  );
}

function itemCanBulkResolveManual(item: CrmActionExecutionQueueItem): boolean {
  return item.executionEligibility?.canResolveManually ?? Boolean(
    item.queueManualResolution
    && item.status === "ready_for_execution"
    && !item.materializationEventId,
  );
}

function itemHasBridgeGap(item: CrmActionExecutionQueueItem): boolean {
  return item.status === "ready_for_execution"
    && !itemCanBulkMaterialize(item)
    && !item.queueMaterialization
    && !item.queueManualResolution
    && item.priority.tier !== "already_materialized";
}

function buildQueueTotals(items: CrmActionExecutionQueueItem[]): CrmActionExecutionQueueTotals {
  return {
    total: items.length,
    readyForExecution: items.filter((item) => item.status === "ready_for_execution").length,
    manualReview: items.filter((item) => item.status === "manual_review").length,
    blocked: items.filter((item) => item.status === "blocked").length,
    unknown: items.filter((item) => item.status === "unknown").length,
    outcomeBackedReady: items.filter((item) => item.priority.tier === "outcome_backed_ready").length,
    materializableReady: items.filter((item) => item.status === "ready_for_execution" && itemCanBulkMaterialize(item)).length,
    manualOnlyReady: items.filter((item) => item.status === "ready_for_execution" && item.queueManualResolution).length,
    bridgeBlockedReady: items.filter(itemHasBridgeGap).length,
    alreadyMaterialized: items.filter((item) => item.priority.tier === "already_materialized").length,
  };
}

function formatSkippedBulkResults(result: BulkMaterializeCrmActionQueueResult): string {
  if (result.skipped === 0) return "";

  const reasonCounts = new Map<string, number>();
  for (const item of result.results) {
    if (item.status !== "skipped") continue;
    const reason = item.reason ?? "not_materializable";
    reasonCounts.set(reason, (reasonCounts.get(reason) ?? 0) + 1);
  }

  const reasonSummary = Array.from(reasonCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([reason, count]) => `${count} ${BULK_SKIP_REASON_LABEL[reason] ?? reason.replace(/_/g, " ")}`)
    .join(", ");

  return reasonSummary ? ` ${result.skipped} skipped (${reasonSummary}).` : ` ${result.skipped} skipped.`;
}

function formatSkippedManualResults(result: BulkResolveManualCrmActionQueueResult): string {
  if (result.skipped === 0) return "";

  const reasonCounts = new Map<string, number>();
  for (const item of result.results) {
    if (item.status !== "skipped") continue;
    const reason = item.reason ?? "not_manual_resolvable";
    reasonCounts.set(reason, (reasonCounts.get(reason) ?? 0) + 1);
  }

  const reasonSummary = Array.from(reasonCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([reason, count]) => `${count} ${BULK_SKIP_REASON_LABEL[reason] ?? reason.replace(/_/g, " ")}`)
    .join(", ");

  return reasonSummary ? ` ${result.skipped} skipped (${reasonSummary}).` : ` ${result.skipped} skipped.`;
}

function formatBulkMaterializeSummary(result: BulkMaterializeCrmActionQueueResult): string {
  const relevantActions = result.results
    .filter((item) => result.dryRun ? item.status === "planned" : item.status === "succeeded")
    .map((item) => prettyActionLabel(item.actionRef));
  const actionSummary = Array.from(new Set(relevantActions)).slice(0, 3).join(", ");
  const suffix = actionSummary ? `: ${actionSummary}` : "";
  const skippedSuffix = formatSkippedBulkResults(result);
  const statusSuffix = result.dryRun ? "" : statusOutcomeSummary(result);

  if (result.dryRun) {
    return `${result.planned} CRM action${result.planned === 1 ? "" : "s"} planned${suffix}.${skippedSuffix}`;
  }

  if (result.failed > 0) {
    const firstFailed = result.results.find((item) => item.status === "failed");
    const firstFailure = firstFailed
      ? ` First failure: ${prettyActionLabel(firstFailed.actionRef)} via ${firstFailed.endpoint ?? "materialization bridge"}.`
      : "";

    return `${result.succeeded} materialized${statusSuffix}, ${result.failed} failed${suffix}.${skippedSuffix}${firstFailure}`;
  }

  return `${result.succeeded} CRM action${result.succeeded === 1 ? "" : "s"} materialized${statusSuffix}${suffix}.${skippedSuffix}`;
}

function formatBulkManualResolutionSummary(result: BulkResolveManualCrmActionQueueResult): string {
  const relevantActions = result.results
    .filter((item) => result.dryRun ? item.status === "planned" : item.status === "succeeded")
    .map((item) => prettyActionLabel(item.actionRef));
  const actionSummary = Array.from(new Set(relevantActions)).slice(0, 3).join(", ");
  const suffix = actionSummary ? `: ${actionSummary}` : "";
  const skippedSuffix = formatSkippedManualResults(result);
  const statusSuffix = result.dryRun ? "" : statusOutcomeSummary(result);

  if (result.dryRun) {
    return `${result.planned} manual resolution${result.planned === 1 ? "" : "s"} planned${suffix}.${skippedSuffix}`;
  }

  if (result.failed > 0) {
    const firstFailed = result.results.find((item) => item.status === "failed");
    const firstFailure = firstFailed
      ? ` First failure: ${prettyActionLabel(firstFailed.actionRef)} via ${firstFailed.endpoint ?? "manual bridge"}.`
      : "";

    return `${result.succeeded} manual resolutions recorded${statusSuffix}, ${result.failed} failed${suffix}.${skippedSuffix}${firstFailure}`;
  }

  return `${result.succeeded} manual resolution${result.succeeded === 1 ? "" : "s"} recorded${statusSuffix}${suffix}.${skippedSuffix}`;
}

function normalizeSourceEventType(value: string | null | undefined): string | null {
  const sourceEventType = value?.trim();
  return sourceEventType ? sourceEventType : null;
}

interface ActionExecutionQueueProps {
  contractRef?: string;
  signalFamily?: string;
  sourceEventType?: string;
  queueView?: QueueView;
  onQueueViewChange?: (view: QueueView) => void;
}

export function ActionExecutionQueue({
  contractRef,
  signalFamily,
  sourceEventType,
  queueView: controlledQueueView,
  onQueueViewChange,
}: ActionExecutionQueueProps = {}) {
  const [localQueueView, setLocalQueueView] = useState<QueueView>("all");
  const queueView = controlledQueueView ?? localQueueView;
  const setQueueView = onQueueViewChange ?? setLocalQueueView;
  const activeQueueFilters = {
    ...QUEUE_VIEW_FILTERS[queueView],
    ...(contractRef ? { actionRef: contractRef } : {}),
    ...(signalFamily ? { signalFamily } : {}),
    ...(sourceEventType ? { sourceEventType } : {}),
  };
  const {
    data,
    isLoading,
    error,
    refetch,
    materializeQueueItem,
    resolveManual,
    bulkMaterialize,
    bulkResolveManual,
  } = useCrmActionExecutionQueue(activeQueueFilters);
  const [materializingId, setMaterializingId] = useState<string | null>(null);
  const [bulkMaterializingMode, setBulkMaterializingMode] = useState<"preview" | "run" | null>(null);
  const [bulkManualResolvingMode, setBulkManualResolvingMode] = useState<"preview" | "run" | null>(null);
  const [materializeError, setMaterializeError] = useState<string | null>(null);
  const [materializeNotice, setMaterializeNotice] = useState<string | null>(null);
  const [sourceEventTypeFilter, setSourceEventTypeFilter] = useState<string>(ALL_SOURCE_EVENTS_FILTER);

  const handleBulkMaterialize = async (itemsToMaterialize: CrmActionExecutionQueueItem[], dryRun: boolean) => {
    if (itemsToMaterialize.length === 0) {
      return;
    }

    setBulkMaterializingMode(dryRun ? "preview" : "run");
    setMaterializeError(null);
    setMaterializeNotice(null);

    try {
      const result = await bulkMaterialize({
        decisionEventIds: itemsToMaterialize.map((item) => item.id),
        limit: itemsToMaterialize.length,
        dryRun,
      });
      const summary = formatBulkMaterializeSummary(result);
      if (result.failed > 0) {
        setMaterializeError(summary);
      } else {
        setMaterializeNotice(summary);
      }
    } catch (caughtError) {
      const message = caughtError instanceof Error
        ? caughtError.message
        : "Failed to bulk materialize CRM actions";
      setMaterializeError(`0 materialized, ${itemsToMaterialize.length} failed. ${message}`);
    }

    setMaterializingId(null);
    setBulkMaterializingMode(null);
    if (!dryRun) {
      await refetch();
    }
  };

  const handleBulkResolveManual = async (itemsToResolve: CrmActionExecutionQueueItem[], dryRun: boolean) => {
    if (itemsToResolve.length === 0) {
      return;
    }

    setBulkManualResolvingMode(dryRun ? "preview" : "run");
    setMaterializeError(null);
    setMaterializeNotice(null);

    try {
      const result = await bulkResolveManual({
        decisionEventIds: itemsToResolve.map((item) => item.id),
        limit: itemsToResolve.length,
        dryRun,
      });
      const summary = formatBulkManualResolutionSummary(result);
      if (result.failed > 0) {
        setMaterializeError(summary);
      } else {
        setMaterializeNotice(summary);
      }
    } catch (caughtError) {
      const message = caughtError instanceof Error
        ? caughtError.message
        : "Failed to bulk resolve manual CRM actions";
      setMaterializeError(`0 manual resolutions recorded, ${itemsToResolve.length} failed. ${message}`);
    }

    setMaterializingId(null);
    setBulkManualResolvingMode(null);
    if (!dryRun) {
      await refetch();
    }
  };

  const handleMaterializeQueueItem = async (item: CrmActionExecutionQueueItem) => {
    const endpoint = item.executionEligibility?.materializationEndpoint;
    if (!endpoint) {
      setMaterializeError(`No materialization endpoint for ${prettyActionLabel(item.actionRef)}`);
      return;
    }

    setMaterializingId(item.id);
    setMaterializeError(null);
    setMaterializeNotice(null);

    try {
      const result = await materializeQueueItem(endpoint, item.recommendationId);
      const resultStatus = resultStatusLabel(materializationResultStatus(result));
      setMaterializeNotice(`${prettyActionLabel(item.actionRef)} ${resultStatus ?? "materialized"}.`);
      await refetch();
    } catch (caughtError) {
      setMaterializeError(caughtError instanceof Error ? caughtError.message : `Failed to materialize ${prettyActionLabel(item.actionRef)}`);
    } finally {
      setMaterializingId(null);
    }
  };

  const handleResolveManual = async (decisionEventId: string, recommendationId: string) => {
    setMaterializingId(decisionEventId);
    setMaterializeError(null);
    setMaterializeNotice(null);

    try {
      const result = await resolveManual(decisionEventId, recommendationId);
      setMaterializeNotice(`${prettyActionLabel(result.actionRef)} manual resolution recorded.`);
      await refetch();
    } catch (caughtError) {
      setMaterializeError(caughtError instanceof Error ? caughtError.message : "Failed to record manual resolution");
    } finally {
      setMaterializingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-48" />
        </div>
        <SkeletonCard className="h-20" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 text-sm text-[var(--color-warning)]">
        Could not load shadow execution queue.
        <button type="button" onClick={refetch} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  const totals = data?.totals ?? EMPTY_QUEUE_TOTALS;
  const allItems = data?.items ?? [];
  const sourceEventCounts = new Map<string, number>();
  for (const item of allItems) {
    const sourceEventType = normalizeSourceEventType(item.sourceEventType);
    if (!sourceEventType) continue;
    sourceEventCounts.set(sourceEventType, (sourceEventCounts.get(sourceEventType) ?? 0) + 1);
  }
  const sourceEventOptions = Array.from(sourceEventCounts.entries())
    .map(([value, count]) => ({ value, count, label: prettyActionLabel(value) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const sourceEventFilterActive = sourceEventTypeFilter !== ALL_SOURCE_EVENTS_FILTER;
  const visibleSourceEventOptions = sourceEventFilterActive
    && !sourceEventOptions.some((option) => option.value === sourceEventTypeFilter)
    ? [
        { value: sourceEventTypeFilter, count: 0, label: prettyActionLabel(sourceEventTypeFilter) },
        ...sourceEventOptions,
      ]
    : sourceEventOptions;
  const showSourceEventFilter = sourceEventOptions.length > 1 || sourceEventFilterActive;
  const sourceFilteredItems = sourceEventFilterActive
    ? allItems.filter((item) => normalizeSourceEventType(item.sourceEventType) === sourceEventTypeFilter)
    : allItems;
  const items = sourceFilteredItems.slice(0, 6);
  const filteredTotals = data?.filteredTotals ?? totals;
  const displayTotals = sourceEventFilterActive ? buildQueueTotals(sourceFilteredItems) : filteredTotals;
  const scopeFilterActive = queueView !== "all" || sourceEventFilterActive;
  const displayScopeBaseTotal = sourceEventFilterActive ? allItems.length : totals.total;
  const ageBuckets = data?.ageBuckets ?? { fresh: 0, aging: 0, stale: 0, critical: 0 };
  const staleQueueCount = ageBuckets.stale + ageBuckets.critical;
  const hasQueueAgeSignal = data?.oldestAgeDays != null || STALE_BUCKETS.some((bucket) => ageBuckets[bucket] > 0);
  const nextExecutable = data?.nextExecutable ?? null;
  const hasOperatorTriage = displayTotals.manualOnlyReady > 0 || displayTotals.bridgeBlockedReady > 0;
  const materializableItems = sourceFilteredItems.filter(itemCanBulkMaterialize);
  const manualResolvableItems = sourceFilteredItems.filter(itemCanBulkResolveManual);
  const bulkActionRunning = bulkMaterializingMode !== null || bulkManualResolvingMode !== null || Boolean(materializingId);

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-[var(--color-ai)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Shadow Execution Queue</h2>
            <span className="rounded-full border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] px-1.5 py-0.5 text-[10px] text-[var(--color-ai)]">
              Staff gated
            </span>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Accepted recommendations staged for inspection. Explicit materializations are recorded as CRM events.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {materializableItems.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => void handleBulkMaterialize(materializableItems, true)}
                disabled={bulkActionRunning}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                {bulkMaterializingMode === "preview" ? "Previewing..." : `Preview ${materializableItems.length}`}
              </button>
              <button
                type="button"
                onClick={() => void handleBulkMaterialize(materializableItems, false)}
                disabled={bulkActionRunning}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 px-3 py-1 text-xs font-medium text-[var(--color-success)] transition-colors hover:bg-[var(--color-success)]/15 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Zap className="h-3.5 w-3.5" />
                {bulkMaterializingMode === "run" ? "Running..." : `Run ${materializableItems.length}`}
              </button>
            </>
          )}
          {manualResolvableItems.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => void handleBulkResolveManual(manualResolvableItems, true)}
                disabled={bulkActionRunning}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--color-warning)]/25 bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-warning)] transition-colors hover:bg-[var(--color-warning)]/10 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Clock3 className="h-3.5 w-3.5" />
                {bulkManualResolvingMode === "preview" ? "Previewing..." : `Preview manual ${manualResolvableItems.length}`}
              </button>
              <button
                type="button"
                onClick={() => void handleBulkResolveManual(manualResolvableItems, false)}
                disabled={bulkActionRunning}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-3 py-1 text-xs font-medium text-[var(--color-warning)] transition-colors hover:bg-[var(--color-warning)]/15 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                {bulkManualResolvingMode === "run" ? "Recording..." : `Mark resolved ${manualResolvableItems.length}`}
              </button>
            </>
          )}
          <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
            {data?.windowDays ?? 30}d
          </span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {scopeFilterActive ? "Shown queue" : "Accepted queue"}
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{displayTotals.total}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-primary)]">
            <TrendingUp className="h-3.5 w-3.5" />
            Outcome-backed
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-primary)]">{displayTotals.outcomeBackedReady}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-success)]">
            <ShieldCheck className="h-3.5 w-3.5" />
            Materializable
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-success)]">{displayTotals.materializableReady}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-warning)]">
            <Clock3 className="h-3.5 w-3.5" />
            Manual-only
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-warning)]">{displayTotals.manualOnlyReady}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-warning)]">
            <Clock3 className="h-3.5 w-3.5" />
            Review
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-warning)]">{displayTotals.manualReview}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-destructive)]">
            <AlertTriangle className="h-3.5 w-3.5" />
            Blocked
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-destructive)]">{displayTotals.blocked}</p>
        </div>
      </div>

      {hasQueueAgeSignal && (
        <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-medium text-[var(--color-foreground)]">Queue age</p>
              <p className="mt-1 text-[var(--color-muted-foreground)]">
                Oldest accepted action is {data?.oldestAgeDays ?? 0}d old
                {data?.oldestQueuedAt ? `, accepted ${formatMonthDay(data.oldestQueuedAt)}` : ""}.
                {staleQueueCount > 0 ? ` ${staleQueueCount} item${staleQueueCount === 1 ? "" : "s"} need stale-queue attention.` : " No stale backlog in this view."}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {STALE_BUCKETS.map((bucket) => (
                <span key={bucket} className={`rounded-full border px-2 py-0.5 ${STALE_BUCKET_CLASS[bucket]}`}>
                  {STALE_BUCKET_LABEL[bucket]} {ageBuckets[bucket]}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {(Object.keys(QUEUE_VIEW_LABEL) as QueueView[]).map((view) => (
          <button
            key={view}
            type="button"
            onClick={() => setQueueView(view)}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${
              queueView === view
                ? "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
            }`}
          >
            {QUEUE_VIEW_LABEL[view]}
          </button>
        ))}
        {scopeFilterActive && (
          <span className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]">
            Showing {displayTotals.total} of {displayScopeBaseTotal}
          </span>
        )}
        {contractRef && (
          <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-1 text-xs text-[var(--color-primary)]">
            Contract {prettyActionLabel(contractRef)}
          </span>
        )}
        {signalFamily && (
          <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-1 text-xs text-[var(--color-primary)]">
            Family {prettyActionLabel(signalFamily)}
          </span>
        )}
        {sourceEventType && (
          <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-1 text-xs text-[var(--color-primary)]">
            Source {prettyActionLabel(sourceEventType)}
          </span>
        )}
        {showSourceEventFilter && (
          <label className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]">
            Source
            <select
              value={sourceEventTypeFilter}
              onChange={(event) => setSourceEventTypeFilter(event.target.value)}
              className="max-w-52 bg-transparent text-xs font-medium text-[var(--color-foreground)] outline-none"
              aria-label="Filter by source event"
            >
              <option value={ALL_SOURCE_EVENTS_FILTER}>All source events</option>
              {visibleSourceEventOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
          </label>
        )}
        {sourceEventFilterActive && (
          <span className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]">
            Source {sourceFilteredItems.length} of {allItems.length} loaded
          </span>
        )}
      </div>

      {materializeError && (
        <p className="mt-4 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-xs text-[var(--color-destructive)]">
          {materializeError}
        </p>
      )}
      {materializeNotice && (
        <p className="mt-4 rounded-md border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-3 py-2 text-xs text-[var(--color-success)]">
          {materializeNotice}
        </p>
      )}

      {hasOperatorTriage && (
        <div className="mt-4 rounded-lg border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-3 py-2 text-xs text-[var(--color-warning)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-medium">Operator triage required</p>
              <p className="mt-1">
                {displayTotals.manualOnlyReady} manual-only accepted actions · {displayTotals.bridgeBlockedReady} accepted actions missing a materialization bridge.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {displayTotals.manualOnlyReady > 0 && (
                <button
                  type="button"
                  onClick={() => setQueueView("manual_only")}
                  className="rounded-md border border-[var(--color-warning)]/30 px-2 py-1 transition-colors hover:bg-[var(--color-warning)]/10"
                >
                  Manual path
                </button>
              )}
              {displayTotals.bridgeBlockedReady > 0 && (
                <button
                  type="button"
                  onClick={() => setQueueView("bridge_blocked")}
                  className="rounded-md border border-[var(--color-warning)]/30 px-2 py-1 transition-colors hover:bg-[var(--color-warning)]/10"
                >
                  Bridge gaps
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {nextExecutable && (
        <div className="mt-4 rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-3 py-2 text-xs text-[var(--color-success)]">
          <p className="font-medium">
            Next executable: {prettyActionLabel(nextExecutable.actionRef)} · priority {nextExecutable.priority.score}
          </p>
          {nextExecutable.queueMaterialization && (
            <p className="mt-1">
              Bridge {prettyOperationLabel(nextExecutable.queueMaterialization.operation)} · {nextExecutable.queueMaterialization.entityType} · {nextExecutable.queueMaterialization.endpoint}
            </p>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)]">Prioritized candidates</p>
          {items.map((item) => {
            const targetLink = targetLinkForQueueItem(item);
            const canResolveManual = itemCanBulkResolveManual(item);

            return (
            <div
              key={item.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs"
            >
              <div className="min-w-0">
                <p className="font-medium text-[var(--color-foreground)]">
                  {prettyActionLabel(item.actionRef)}
                </p>
                <p className="text-[var(--color-muted-foreground)]">
                  {prettyActionLabel(item.intent)} - accepted {formatMonthDay(item.acceptedAt)}
                  {item.sourceEventType ? ` - source ${prettyActionLabel(item.sourceEventType)}` : ""}
                  {item.signalFamily ? ` - ${prettyActionLabel(item.signalFamily)} family` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {item.staleBucket && (
                  <span className={`rounded-full border px-2 py-0.5 ${STALE_BUCKET_CLASS[item.staleBucket]}`}>
                    {formatQueueAge(item.ageHours, item.ageDays)}
                  </span>
                )}
                <span className={`rounded-full border px-2 py-0.5 ${PRIORITY_CLASS[item.priority.tier] ?? PRIORITY_CLASS.unknown}`}>
                  {PRIORITY_LABEL[item.priority.tier] ?? PRIORITY_LABEL.unknown}
                </span>
                <span className={`rounded-full border px-2 py-0.5 ${STATUS_CLASS[item.status]}`}>
                  {STATUS_LABEL[item.status]}
                </span>
                {item.queueManualResolution && (
                  <span className="rounded-full border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-2 py-0.5 text-[var(--color-warning)]">
                    Manual-only
                  </span>
                )}
                {item.materializationEventId && item.materializedAt && (
                  <span className="rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-2 py-0.5 text-[var(--color-success)]">
                    Recorded {materializationStatusLabel(item.materializationStatus)} {formatMonthDay(item.materializedAt)}
                  </span>
                )}
                {item.observedOutcome && item.observedOutcome.confidence !== "none" && (
                  <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-0.5 text-[var(--color-primary)]">
                    {formatRate(item.observedOutcome.materializationRate)} materialized - {item.observedOutcome.confidence}
                    {item.observedOutcome.evidenceAgeDays == null ? "" : ` - ${item.observedOutcome.evidenceAgeDays}d old`}
                  </span>
                )}
                {item.outcomeScoring && item.outcomeScoring.adjustedConfidence !== item.outcomeScoring.baseConfidence && (
                  <span className="rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-2 py-0.5 text-[var(--color-success)]">
                    {formatRate(item.outcomeScoring.adjustedConfidence)} outcome-adjusted
                  </span>
                )}
                {item.executionEligibility && (
                  <span className={`rounded-full border px-2 py-0.5 ${ELIGIBILITY_CLASS[item.executionEligibility.state]}`}>
                    {ELIGIBILITY_LABEL[item.executionEligibility.state]}
                  </span>
                )}
                {itemCanBulkMaterialize(item) && item.executionEligibility?.materializationEndpoint && (
                  <button
                    type="button"
                    onClick={() => handleMaterializeQueueItem(item)}
                    disabled={materializingId === item.id}
                    className="rounded-md border border-[var(--color-success)]/30 px-2 py-0.5 text-[var(--color-success)] transition-colors hover:bg-[var(--color-success)]/10 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {materializingId === item.id
                      ? "Applying..."
                      : `Run ${prettyOperationLabel(item.queueMaterialization?.operation ?? "materialization")}`}
                  </button>
                )}
                {canResolveManual && (
                  <button
                    type="button"
                    onClick={() => handleResolveManual(item.id, item.recommendationId)}
                    disabled={materializingId === item.id}
                    className="rounded-md border border-[var(--color-warning)]/30 px-2 py-0.5 text-[var(--color-warning)] transition-colors hover:bg-[var(--color-warning)]/10 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {materializingId === item.id ? "Recording..." : "Mark resolved"}
                  </button>
                )}
                {item.materializedTaskId && (
                  <Link
                    to="/crm/tasks"
                    className="text-[var(--color-success)] hover:underline"
                  >
                    Task {item.materializedTaskState ?? "recorded"}
                  </Link>
                )}
                {item.materializedActivityState && (
                  <Link
                    to="/crm/activities"
                    className="text-[var(--color-success)] hover:underline"
                  >
                    Activity {item.materializedActivityState === "existing" ? "existing" : "logged"}
                  </Link>
                )}
                {item.materializedTagState && (
                  <span className="text-[var(--color-success)]">
                    Tag {item.materializedTagState}
                  </span>
                )}
                {item.materializedDealState && (
                  <Link
                    to={item.materializedDealId ? `/crm/deals/${item.materializedDealId}` : "/crm/deals"}
                    className="text-[var(--color-success)] hover:underline"
                  >
                    Deal {item.materializedDealState === "stage_updated" ? "stage updated" : item.materializedDealState ?? "recorded"}
                  </Link>
                )}
                {item.materializedPersonState && (
                  <Link
                    to={item.materializedPersonId ? `/crm/contacts/${item.materializedPersonId}` : "/crm/contacts"}
                    className="text-[var(--color-success)] hover:underline"
                  >
                    Person {item.materializedPersonState ?? "recorded"}
                  </Link>
                )}
                {item.materializedCompanyState && (
                  <Link
                    to={item.materializedCompanyId ? `/crm/companies/${item.materializedCompanyId}` : "/crm/companies"}
                    className="text-[var(--color-success)] hover:underline"
                  >
                    Company {item.materializedCompanyState ?? "recorded"}
                  </Link>
                )}
                {targetLink && (
                  <Link
                    to={targetLink.to}
                    className="text-[var(--color-primary)] hover:underline"
                  >
                    {targetLink.label}
                  </Link>
                )}
              </div>
              {item.executionPlanReason && (
                <p className="basis-full text-[var(--color-muted-foreground)]">
                  {item.executionPlanReason}
                </p>
              )}
              {item.materializationBlockers.length > 0 && (
                <p className="basis-full text-[var(--color-warning)]">
                  {item.queueManualResolution ? "Manual path" : "Blocked bridge"}: {item.materializationBlockers.slice(0, 2).join(" ")}
                </p>
              )}
              {item.executionEligibility?.materializationEndpoint && (
                <p className="basis-full text-[var(--color-muted-foreground)]">
                  Staff-gated endpoint: {item.executionEligibility.materializationEndpoint}
                </p>
              )}
              {item.queueManualResolution && (
                <p className="basis-full rounded-md border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-2 py-1 text-[var(--color-warning)]">
                  Operator action: {item.queueManualResolution.operatorAction}
                </p>
              )}
              {!item.queueMaterialization && !item.queueManualResolution && item.status === "ready_for_execution" && (
                <p className="basis-full rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-2 py-1 text-[var(--color-destructive)]">
                  Bridge gap: no materialization bridge is registered for {prettyActionLabel(item.actionRef)} yet.
                </p>
              )}
              <p className="basis-full text-[var(--color-muted-foreground)]">
                Priority {item.priority.score}: {item.priority.reason}
              </p>
            </div>
            );
          })}
        </div>
      )}
      {items.length === 0 && (
        <div className="mt-4 rounded-lg border border-dashed border-[var(--color-border)] px-3 py-4 text-center text-sm text-[var(--color-muted-foreground)]">
          {sourceEventFilterActive
            ? `No prioritized candidates from ${prettyActionLabel(sourceEventTypeFilter)} in this view.`
            : "No prioritized candidates in this view."}
        </div>
      )}
    </div>
  );
}
